import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArchive,
  lucideArchiveRestore,
  lucideArrowDown,
  lucideArrowUp,
  lucideEllipsis,
  lucideEye,
  lucideEyeOff,
  lucideFileText,
  lucideHistory,
  lucideImage,
  lucidePencil,
  lucideShoppingCart,
} from '@ng-icons/lucide';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { HlmSheetImports } from '@spartan-ng/helm/sheet';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { VoucherProduct } from '../../services/voucher-product.service';

@Component({
  selector: 'app-product-card',
  standalone: true,
  imports: [
    NgIcon,
    HlmBadgeImports,
    HlmButtonImports,
    HlmCardImports,
    HlmDropdownMenuImports,
    HlmSeparatorImports,
    HlmSheetImports,
    HlmSpinnerImports,
  ],
  providers: [
    provideIcons({
      lucideArchive,
      lucideArchiveRestore,
      lucideArrowDown,
      lucideArrowUp,
      lucideEllipsis,
      lucideEye,
      lucideEyeOff,
      lucideFileText,
      lucideHistory,
      lucideImage,
      lucidePencil,
      lucideShoppingCart,
    }),
  ],
  templateUrl: './product-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./product-card.component.css'],
})
export class ProductCardComponent {
  @Input() product!: VoucherProduct;
  @Input() isArchived: boolean = false;
  @Input() loading: boolean = false;
  @Input() isDownloadingExample: boolean = false;
  @Input() position?: number; // Position number (1-indexed for display)
  @Input() isFirst: boolean = false; // Is this the first item?
  @Input() isLast: boolean = false; // Is this the last item?
  @Input() getImageUrl!: (imagePath: string | null, updatedAt?: string) => string | null;
  @Input() formatPrice!: (priceInCents: number) => string;

  @Output() edit = new EventEmitter<VoucherProduct>();
  @Output() toggleStatus = new EventEmitter<VoucherProduct>();
  @Output() archive = new EventEmitter<VoucherProduct>();
  @Output() unarchive = new EventEmitter<VoucherProduct>();
  @Output() moveLeft = new EventEmitter<VoucherProduct>();
  @Output() moveRight = new EventEmitter<VoucherProduct>();
  @Output() viewVersionHistory = new EventEmitter<VoucherProduct>();
  @Output() downloadExample = new EventEmitter<VoucherProduct>();

  actionsSheetOpen = false;
  private pendingAction: (() => void) | null = null;

  get hasDiscount(): boolean {
    return (this.product?.discount_percentage ?? 0) > 0;
  }

  /** Price (cents) the customer pays; rounds half-to-even like the backend. */
  get discountedPrice(): number {
    return Math.round((this.product.price * (100 - this.product.discount_percentage)) / 100);
  }

  /** Runs after the sheet has closed, so its focus restore can't steal the next dialog's focus. */
  chooseAction(action: () => void) {
    this.pendingAction = action;
    this.actionsSheetOpen = false;
  }

  runPendingAction() {
    const action = this.pendingAction;
    this.pendingAction = null;
    action?.call(this);
  }

  onEdit() {
    this.edit.emit(this.product);
  }

  onToggleStatus() {
    this.toggleStatus.emit(this.product);
  }

  onArchive() {
    this.archive.emit(this.product);
  }

  onUnarchive() {
    this.unarchive.emit(this.product);
  }

  onMoveLeft() {
    this.moveLeft.emit(this.product);
  }

  onMoveRight() {
    this.moveRight.emit(this.product);
  }

  onViewVersionHistory() {
    this.viewVersionHistory.emit(this.product);
  }

  onDownloadExample() {
    this.downloadExample.emit(this.product);
  }
}
